/**
 * Auth utilities — Cognito PKCE flow integration.
 *
 * Security (security.md §1):
 *   - Tokens stored in memory ONLY (not localStorage/sessionStorage)
 *   - PKCE flow via Cognito hosted UI
 *   - Refresh-token revocation via Cognito sign-out endpoint
 */

import { setAccessToken, getAccessToken } from './api';

const COGNITO_DOMAIN = import.meta.env.VITE_COGNITO_DOMAIN ?? '';
const CLIENT_ID = import.meta.env.VITE_COGNITO_CLIENT_ID ?? '';
const REDIRECT_URI = import.meta.env.VITE_REDIRECT_URI ?? `${window.location.origin}/callback`;

// PKCE helpers
function generateCodeVerifier(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return btoa(String.fromCharCode(...array))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const hash = await crypto.subtle.digest('SHA-256', data);
  return btoa(String.fromCharCode(...new Uint8Array(hash)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

export async function initiateLogin(): Promise<void> {
  const verifier = generateCodeVerifier();
  const challenge = await generateCodeChallenge(verifier);

  // Store verifier in sessionStorage temporarily (only during the auth flow)
  // This is acceptable — it's a one-time-use PKCE verifier, not a token.
  sessionStorage.setItem('pkce_verifier', verifier);

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: CLIENT_ID,
    redirect_uri: REDIRECT_URI,
    scope: 'openid email profile',
    code_challenge: challenge,
    code_challenge_method: 'S256',
    state: crypto.randomUUID(), // CSRF protection
  });

  window.location.href = `${COGNITO_DOMAIN}/login?${params.toString()}`;
}

export async function handleCallback(code: string): Promise<boolean> {
  const verifier = sessionStorage.getItem('pkce_verifier');
  sessionStorage.removeItem('pkce_verifier');

  if (!verifier) {
    console.error('Missing PKCE verifier');
    return false;
  }

  try {
    const response = await fetch(`${COGNITO_DOMAIN}/oauth2/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        grant_type: 'authorization_code',
        client_id: CLIENT_ID,
        code,
        redirect_uri: REDIRECT_URI,
        code_verifier: verifier,
      }).toString(),
    });

    if (!response.ok) return false;

    const tokens = await response.json();

    // Store id_token in memory — API Gateway Cognito authoriser validates id_token
    // (not access_token) when configured with identity source = Authorization header
    setAccessToken(tokens.id_token);

    return true;
  } catch (err) {
    console.error('Token exchange failed:', err);
    return false;
  }
}

export function logout(): void {
  setAccessToken(null);
  // Simple logout: clear token and redirect to login page
  // The Cognito session cookie will expire naturally (or user clears cookies)
  window.location.href = '/login';
}

export function isAuthenticated(): boolean {
  // In-memory check — token exists
  return !!getAccessToken();
}
